import type { Page } from '../../types';
import {
  disposalDeriskingOptions,
  disposalForecastConfidenceOptions,
  disposalForecastYearOptions,
} from './data';

const receiptForecasting: Page[] = [
  {
    pageType: 'question',
    title: 'Receipt forecasting',
    name: 'receipt-forecasting',
    description:
      'Provide the forecast timing and value information that OGP will use for disposal pipeline analysis.',
    questions: [
      {
        questionType: 'select',
        title: 'Level of de-risking activity required',
        name: 'level-of-derisking-activity-required',
        hintOption: 'Select a level',
        options: disposalDeriskingOptions,
        detailsHeading: 'How to choose a level',
        detailsContent: `<p><strong>Significant</strong> = site may be occupied, no formal planning process to vacate site has commenced; desired planning permission may be contentious (incl. Green Belt) or is outside local plan/policy; and/or high risk level of decontamination/ remediation; and/or high capital enabling or reprovision costs required/yet to be allocated; and/or works are required to secure retained assets e.g. erect security perimeter, re-routing services; and/or complex disposal method e.g. JV or developer agreement; and/or disposal may be subject to other dependencies: e.g. significant business service change.</p>

<p><strong>Moderate</strong> = where a number of de-risking challenges are present such that the level of activity is judged neither low nor significant e.g. plans in place to vacate site and planning consent likely to be granted, but reprovision funding not yet secured and site will be disposed via development partnership.</p>

<p><strong>Minor</strong> = site vacant or approved plans in place to relocate; detailed planning consent obtained/ uncontentious use/ is not required; and/or ownership clear; and/or few encumbrances; and/or little or no site remediation or securing of retained assets required; and/or low value or secured funding; and/or straightforward disposal method chosen e.g. auction; and/or operational requirements clear.</p>

<p>When the sale has completed unconditionally, replace the level of de-risking activity required with the entry <strong>Sold</strong></p>`,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Unconditional contract forecast date (Year)',
        name: 'unconditional-contract-forecast-date-year',
        hintOption: 'Select a financial year',
        options: disposalForecastYearOptions,
        hint: 'This is the financial year in which the contract is expected to become unconditional.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Forecast receipt value (GBP)',
        name: 'forecast-receipt-value-gbp',
        hint: 'Enter the expected gross capital receipt as a number only.',
        placeholder: '0.00',
        rules: {
          required: true,
          float: true,
        },
      },
      {
        questionType: 'select',
        title: 'Forecast receipt value confidence rating',
        name: 'forecast-receipt-value-confidence-rating',
        hintOption: 'Select a confidence rating',
        options: disposalForecastConfidenceOptions,
        detailsHeading: 'Confidence rating guidance',
        detailsContent: `<p><strong>Low</strong> = Limited initial valuation assessment (e.g. high level desktop valuation); or no advice has been provided from a suitably qualified property professional or from the HMG property function to inform the forecast value. Planning position may be uncertain e.g. in Green Belt or may be otherwise contentious. Not market tested.</p>

<p><strong>Medium</strong> = Forecast market value is based on a valuation prepared by a suitably qualified property professional or an estimate of value prepared by a property professional. Where valuations or estimates of value contain materially significant uncosted assumptions e.g. land contamination or remediation then a Low confidence rating should be used.<br /><br />Where valuations or estimates of value are particularly out of date (e.g. greater than 3 years), or it is understood that valuation inputs and assumptions significantly differ from recent or current market activity then the forecast receipt should be adjusted to reflect this. Where valuation adjustment is not possible due to complexity of factors then a Low confidence rating should be used.<br /><br />OR<br /><br />Recent Red Book market valuation but planning position may be uncertain e.g. in Green Belt or may be otherwise contentious.</p>

<p><strong>High</strong> = Recent Red Book market valuation, aligned to the most likely permitted future use of the site/ planned end-use in site disposal strategy. Site has been market tested. High level of planning confidence e.g. consented or not required.</p>

<p>When the sale has completed unconditionally, replace the confidence rating with the entry <strong>Sold</strong></p>`,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'dynamicGrid',
        title: 'Dynamic grid example: disposal receipt lines',
        name: 'dynamic-grid-example-disposal-receipt-lines',
        hint: 'Use this test grid to paste rows from Excel, edit cells, add rows and see the saved structured row data.',
        detailsHeading: 'Test grid',
        detailsContent:
          'This is an optional example only. It captures rows from the question dynamicGrid config and does not block submission.',
        dynamicGrid: {
          minRows: 2,
          maxRows: 8,
          height: 360,
          addButtonText: 'Add receipt line',
          columns: [
            {
              field: 'siteName',
              headerName: 'Site name',
              type: 'text',
              required: true,
              minWidth: 180,
            },
            {
              field: 'forecastYear',
              headerName: 'Forecast year',
              type: 'select',
              options: disposalForecastYearOptions,
              minWidth: 170,
            },
            {
              field: 'confidence',
              headerName: 'Confidence',
              type: 'select',
              options: disposalForecastConfidenceOptions,
              minWidth: 150,
            },
            {
              field: 'forecastReceiptValue',
              headerName: 'Forecast receipt value',
              type: 'number',
              prefix: '\u00a3',
              total: true,
              minWidth: 190,
            },
            {
              field: 'comment',
              headerName: 'Comment',
              type: 'text',
              minWidth: 220,
            },
          ],
          rows: [
            {
              siteName: 'Example site A',
              forecastYear: '25/26',
              confidence: 'Medium',
              forecastReceiptValue: '1250000',
              comment: 'Paste over these rows from Excel.',
            },
            {
              siteName: 'Example site B',
              forecastYear: '26/27',
              confidence: 'High',
              forecastReceiptValue: '875000',
              comment: '',
            },
          ],
        },
      },
    ],
  },
];

export default receiptForecasting;
